import {useEffect, useState} from 'react'
import {useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'

import {Loading} from '../../../../../../_metronic/elements/Loading'
import {Modal} from '../../../../../../_metronic/elements/Modal'
import {openAlert} from '../../../../../../_metronic/elements/NotificationAlert'
import {useErrorHandler} from '../../../../../../_metronic/helpers/ErrorHandler'
import {KTSVG} from '../../../../../../_metronic/helpers'
import {formatFixedNumber} from '../../../../../shared/numberFormat'
import {useIsMobile} from '../../../../../shared/responsive'
import ReportDeleteConfirmation from '../../ReportDeleteConfirmation'
import * as ReportService from '../../services/ReportService'
import {useEconomicReportContext} from '../EconomicReportContext'
import {
  filterDuplicateSurfaceReconciliationWarnings,
  getSurfaceReconciliationConsistencyIssues,
  mergeSurfaceReconciliationForDisplay,
  normalizeSurfaceReconciliation,
} from './surfaceReconciliationDisplay'
import {useViewEconomicReportContext, ViewEconomicReportProvider} from './ViewEconomicReportContext'

const currency = (value) => `${formatFixedNumber(value)} USD`
const usdHa = (value) => `${formatFixedNumber(value)} USD/ha`
const tonsHa = (value) => `${formatFixedNumber(value, 2)} t/ha`
const hectares = (value) => `${formatFixedNumber(value, 2)} ha`
const integer = (value) => (value === null || value === undefined ? '-' : formatFixedNumber(value, 0))
const numberOrDash = (value, decimals = 2, suffix = '') =>
  value === null || value === undefined ? '-' : `${formatFixedNumber(value, decimals)}${suffix}`

const INPUT_UNIT_LABELS = {
  ton_per_ha: 't/ha',
  kg_per_ha: 'kg/ha',
  qq_per_ha: 'qq/ha',
  seeds_per_ha: 'sem/ha',
  thousand_seeds_per_ha: 'mil semillas/ha',
  bags_per_ha: 'bolsas/ha',
  l_per_ha: 'l/ha',
}

const MOISTURE_V2_INPUT_YIELD_BASES = new Set([
  'as_harvested',
  'corrected_excess_only',
  'normalized_to_target',
])

const hasDisplayValue = (value) => value !== null && value !== undefined && value !== ''

const surfaceCoverageOrDash = (surfaceHa, coveragePercent) => {
  const surface = numberOrDash(surfaceHa, 2, ' ha')
  if (!hasDisplayValue(coveragePercent)) {
    return surface
  }

  return `${surface} (${numberOrDash(coveragePercent, 2, ' %')})`
}

const formatInputNumber = (value, suffix = '', decimals = 2) =>
  hasDisplayValue(value) ? `${formatFixedNumber(value, decimals)}${suffix}` : null

const inputUnit = (value) => INPUT_UNIT_LABELS[value] || value || null

const getEconomicReportStatusClass = (status) =>
  ({
    billing_pending: 'badge-light-primary',
    completed: 'badge-light-success',
    completed_with_warnings: 'badge-light-warning',
    error: 'badge-light-danger',
    scheduled: 'badge-light-primary',
    processing_files: 'badge-light-info',
    refund_pending: 'badge-light-warning',
    calculating: 'badge-light-info',
    generating_map: 'badge-light-info',
    generating_pdf: 'badge-light-info',
  }[status] || 'badge-light-secondary')

const rangeOrDash = (min, max, decimals = 2, suffix = '') => {
  if (min === null || min === undefined || max === null || max === undefined) {
    return '-'
  }

  return `${formatFixedNumber(min, decimals)} - ${formatFixedNumber(max, decimals)}${suffix}`
}

const getNumberValue = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined || value === '') {
      continue
    }

    const normalizedValue =
      typeof value === 'string'
        ? Number(value.includes(',') ? value.replace(/\./g, '').replace(',', '.') : value)
        : Number(value)

    if (Number.isFinite(normalizedValue)) {
      return normalizedValue
    }
  }

  return undefined
}

const getValue = (object, ...keys) => {
  if (!object) {
    return undefined
  }

  return keys.reduce((value, key) => {
    if (value !== undefined && value !== null) {
      return value
    }

    return object[key]
  }, undefined)
}

const normalizeArtifact = (artifact) => {
  if (!artifact) {
    return null
  }

  return {
    ...artifact,
    contentType: getValue(artifact, 'contentType', 'content_type'),
    downloadPath: getValue(artifact, 'downloadPath', 'download_path'),
    filename: getValue(artifact, 'filename', 'file_name'),
  }
}

const normalizeTotals = (totals = {}) => {
  const source = totals || {}

  return {
    ...source,
    directCostUsdHa: getValue(source, 'directCostUsdHa', 'direct_cost_usd_ha'),
    fixedCostUsdHa: getValue(source, 'fixedCostUsdHa', 'fixed_cost_usd_ha'),
    totalCostUsd: getValue(source, 'totalCostUsd', 'total_cost_usd'),
    totalCostUsdHa: getValue(source, 'totalCostUsdHa', 'total_cost_usd_ha'),
    variableCostUsdHa: getValue(source, 'variableCostUsdHa', 'variable_cost_usd_ha'),
    grossMarginUsdHa: getValue(source, 'grossMarginUsdHa', 'gross_margin_usd_ha'),
    grossMarginUsdTotal: getValue(source, 'grossMarginUsdTotal', 'gross_margin_usd_total'),
    pointCount: getValue(source, 'pointCount', 'point_count'),
    revenueUsdHa: getValue(source, 'revenueUsdHa', 'revenue_usd_ha'),
    surfaceHa: getValue(source, 'surfaceHa', 'surface_ha'),
    harvestedYieldTonsHa: getValue(
      source,
      'harvestedYieldTonsHa',
      'harvested_yield_tons_ha'
    ),
    layerYieldTonsHa: getValue(source, 'layerYieldTonsHa', 'layer_yield_tons_ha'),
    economicYieldTonsHa: getValue(
      source,
      'economicYieldTonsHa',
      'economic_yield_tons_ha'
    ),
    harvestedTotalTons: getValue(source, 'harvestedTotalTons', 'harvested_total_tons'),
    layerTotalTons: getValue(source, 'layerTotalTons', 'layer_total_tons'),
    economicTotalTons: getValue(source, 'economicTotalTons', 'economic_total_tons'),
    seedDose: getValue(source, 'seedDose', 'seed_dose'),
    fertilizer1Dose: getValue(source, 'fertilizer1Dose', 'fertilizer_1_dose'),
    fertilizer2Dose: getValue(source, 'fertilizer2Dose', 'fertilizer_2_dose'),
    yieldTonsHa: getValue(source, 'yieldTonsHa', 'yield_tons_ha'),
  }
}

const normalizeEnvironment = (environment = {}) => {
  const source = environment || {}

  return {
    ...source,
    directCostUsdHa: getValue(source, 'directCostUsdHa', 'direct_cost_usd_ha'),
    environmentId: getValue(source, 'environmentId', 'environment_id', 'id'),
    environmentName: getValue(source, 'environmentName', 'environment_name', 'name'),
    fixedCostUsdHa: getValue(source, 'fixedCostUsdHa', 'fixed_cost_usd_ha'),
    totalCostUsd: getValue(source, 'totalCostUsd', 'total_cost_usd'),
    totalCostUsdHa: getValue(source, 'totalCostUsdHa', 'total_cost_usd_ha'),
    variableCostUsdHa: getValue(source, 'variableCostUsdHa', 'variable_cost_usd_ha'),
    grossMarginUsdHa: getValue(source, 'grossMarginUsdHa', 'gross_margin_usd_ha'),
    grossMarginUsdTotal: getValue(source, 'grossMarginUsdTotal', 'gross_margin_usd_total'),
    pointCount: getValue(source, 'pointCount', 'point_count'),
    revenueUsdHa: getValue(source, 'revenueUsdHa', 'revenue_usd_ha'),
    surfaceHa: getValue(source, 'surfaceHa', 'surface_ha'),
    harvestedYieldTonsHa: getValue(
      source,
      'harvestedYieldTonsHa',
      'harvested_yield_tons_ha'
    ),
    layerYieldTonsHa: getValue(source, 'layerYieldTonsHa', 'layer_yield_tons_ha'),
    economicYieldTonsHa: getValue(
      source,
      'economicYieldTonsHa',
      'economic_yield_tons_ha'
    ),
    seedDose: getValue(source, 'seedDose', 'seed_dose'),
    fertilizer1Dose: getValue(source, 'fertilizer1Dose', 'fertilizer_1_dose'),
    fertilizer2Dose: getValue(source, 'fertilizer2Dose', 'fertilizer_2_dose'),
    yieldTonsHa: getValue(source, 'yieldTonsHa', 'yield_tons_ha'),
  }
}

const sortEnvironmentsByGrossMargin = (environments = []) =>
  [...environments].sort((environmentA, environmentB) => {
    const grossMarginA = getNumberValue(environmentA.grossMarginUsdHa)
    const grossMarginB = getNumberValue(environmentB.grossMarginUsdHa)

    if (
      grossMarginA !== undefined &&
      grossMarginB !== undefined &&
      grossMarginA !== grossMarginB
    ) {
      return grossMarginB - grossMarginA
    }

    if (grossMarginA !== undefined) {
      return -1
    }

    if (grossMarginB !== undefined) {
      return 1
    }

    const nameA = environmentA.environmentName || environmentA.environmentId || ''
    const nameB = environmentB.environmentName || environmentB.environmentId || ''

    return String(nameA).localeCompare(String(nameB), 'es', {
      numeric: true,
      sensitivity: 'base',
    })
  })

const normalizeQuality = (quality = {}) => {
  const source = quality || {}
  const correction = getValue(source, 'yieldCorrection', 'yield_correction') || {}
  const correctionConfiguration = getValue(correction, 'configuration', 'config') || {}
  const correctionMoistureTreatment =
    getValue(correction, 'moistureTreatment', 'moisture_treatment') || {}
  const totalHarvestedTonsBasis =
    getValue(correction, 'totalHarvestedTonsBasis', 'total_harvested_tons_basis') ||
    getValue(
      correctionConfiguration,
      'totalHarvestedTonsBasis',
      'total_harvested_tons_basis'
    )
  const legacyTotalHarvestedTonsCorrected = getValue(
    correction,
    'totalHarvestedTonsCorrected',
    'total_harvested_tons_corrected'
  )
  const explicitCommercialMoistureTotal = getValue(
    correction,
    'totalHarvestedTonsCommercialMoisture',
    'total_harvested_tons_commercial_moisture'
  )
  let totalHarvestedTonsCommercialMoisture = explicitCommercialMoistureTotal
  if (
    !hasDisplayValue(totalHarvestedTonsCommercialMoisture) &&
    totalHarvestedTonsBasis === 'commercial_moisture'
  ) {
    totalHarvestedTonsCommercialMoisture = legacyTotalHarvestedTonsCorrected
  }
  const normalizedCorrection = {
    ...correction,
    moistureTreatment: {
      ...correctionMoistureTreatment,
      inputYieldBasis: getValue(
        correctionMoistureTreatment,
        'inputYieldBasis',
        'input_yield_basis'
      ),
      originalMoistureField: getValue(
        correctionMoistureTreatment,
        'originalMoistureField',
        'original_moisture_field'
      ),
      inputCorrectionTargetPercent: getValue(
        correctionMoistureTreatment,
        'inputCorrectionTargetPercent',
        'input_correction_target_percent'
      ),
      outputCommercialTargetPercent: getValue(
        correctionMoistureTreatment,
        'outputCommercialTargetPercent',
        'output_commercial_target_percent'
      ),
      effectiveCommercialTargetPercent: getValue(
        correctionMoistureTreatment,
        'effectiveCommercialTargetPercent',
        'effective_commercial_target_percent'
      ),
      moistureFieldState: getValue(
        correctionMoistureTreatment,
        'moistureFieldState',
        'moisture_field_state'
      ),
      physicalReconstructionReliable: getValue(
        correctionMoistureTreatment,
        'physicalReconstructionReliable',
        'physical_reconstruction_reliable'
      ),
      layerPolicy: getValue(
        correctionMoistureTreatment,
        'layerPolicy',
        'layer_policy'
      ),
      economicPolicy: getValue(
        correctionMoistureTreatment,
        'economicPolicy',
        'economic_policy'
      ),
      harvestedTotalTons: getValue(
        correctionMoistureTreatment,
        'harvestedTotalTons',
        'harvested_total_tons'
      ),
      layerTotalTons: getValue(
        correctionMoistureTreatment,
        'layerTotalTons',
        'layer_total_tons'
      ),
      economicTotalTons: getValue(
        correctionMoistureTreatment,
        'economicTotalTons',
        'economic_total_tons'
      ),
    },
    rawMinTonsHa: getValue(correction, 'rawMinTonsHa', 'raw_min_tons_ha'),
    rawMaxTonsHa: getValue(correction, 'rawMaxTonsHa', 'raw_max_tons_ha'),
    cleanedMinTonsHa: getValue(correction, 'cleanedMinTonsHa', 'cleaned_min_tons_ha'),
    cleanedMaxTonsHa: getValue(correction, 'cleanedMaxTonsHa', 'cleaned_max_tons_ha'),
    correctedMinTonsHa: getValue(correction, 'correctedMinTonsHa', 'corrected_min_tons_ha'),
    correctedMaxTonsHa: getValue(correction, 'correctedMaxTonsHa', 'corrected_max_tons_ha'),
    sourceMoistureMinPercent: getValue(
      correction,
      'sourceMoistureMinPercent',
      'source_moisture_min_percent',
      'sourceMoistureMin',
      'source_moisture_min',
      'rawMoistureMinPercent',
      'raw_moisture_min_percent',
      'moistureMinPercent',
      'moisture_min_percent'
    ),
    sourceMoistureMaxPercent: getValue(
      correction,
      'sourceMoistureMaxPercent',
      'source_moisture_max_percent',
      'sourceMoistureMax',
      'source_moisture_max',
      'rawMoistureMaxPercent',
      'raw_moisture_max_percent',
      'moistureMaxPercent',
      'moisture_max_percent'
    ),
    correctedMoistureMinPercent: getValue(
      correction,
      'correctedMoistureMinPercent',
      'corrected_moisture_min_percent',
      'targetMoistureMinPercent',
      'target_moisture_min_percent'
    ),
    correctedMoistureMaxPercent: getValue(
      correction,
      'correctedMoistureMaxPercent',
      'corrected_moisture_max_percent',
      'targetMoistureMaxPercent',
      'target_moisture_max_percent'
    ),
    targetMoisturePercent: getValue(
      correction,
      'targetMoisturePercent',
      'target_moisture_percent'
    ),
    totalHarvestedTonsBeforeCorrection: getValue(
      correction,
      'totalHarvestedTonsBeforeCorrection',
      'total_harvested_tons_before_correction'
    ),
    totalHarvestedTonsBeforeCalibration: getValue(
      correction,
      'totalHarvestedTonsBeforeCalibration',
      'total_harvested_tons_before_calibration',
      'totalHarvestedTonsBeforeCorrection',
      'total_harvested_tons_before_correction'
    ),
    totalHarvestedTonsCorrected: getValue(
      correction,
      'totalHarvestedTonsCorrected',
      'total_harvested_tons_corrected'
    ),
    totalHarvestedTonsAfterCalibration: getValue(
      correction,
      'totalHarvestedTonsAfterCalibration',
      'total_harvested_tons_after_calibration',
      'totalHarvestedTonsCorrected',
      'total_harvested_tons_corrected'
    ),
    totalHarvestedTonsCommercialMoisture,
    totalHarvestedTonsBasis,
    totalHarvestedTonsFactor: getValue(
      correction,
      'totalHarvestedTonsFactor',
      'total_harvested_tons_factor',
      'tonFactor'
    ),
  }

  return {
    ...source,
    polygonsWithoutYield: getValue(source, 'polygonsWithoutYield', 'polygons_without_yield'),
    yieldCorrection: normalizedCorrection,
    yieldPointsRemovedByMax: getValue(source, 'yieldPointsRemovedByMax', 'yield_points_removed_by_max'),
    yieldPointsRemovedByMin: getValue(source, 'yieldPointsRemovedByMin', 'yield_points_removed_by_min'),
    yieldPoints: getValue(source, 'yieldPoints', 'yield_points'),
    yieldPointsOutsidePrescription: getValue(
      source,
      'yieldPointsOutsidePrescription',
      'yield_points_outside_prescription'
    ),
    yieldPointsOutsideLand: getValue(
      source,
      'yieldPointsOutsideLand',
      'yield_points_outside_land'
    ),
    surfaceWithYieldHa: getValue(
      source,
      'surfaceWithYieldHa',
      'surface_with_yield_ha',
      'yieldCoverageSurfaceHa',
      'yield_coverage_surface_ha'
    ),
    surfaceWithoutYieldHa: getValue(
      source,
      'surfaceWithoutYieldHa',
      'surface_without_yield_ha'
    ),
    landSurfaceHa: getValue(source, 'landSurfaceHa', 'land_surface_ha'),
    prescriptionSurfaceHa: getValue(
      source,
      'prescriptionSurfaceHa',
      'prescription_surface_ha'
    ),
    prescriptionSurfaceOutsideLandHa: getValue(
      source,
      'prescriptionSurfaceOutsideLandHa',
      'prescription_surface_outside_land_ha'
    ),
    landWithoutPrescriptionHa: getValue(
      source,
      'landWithoutPrescriptionHa',
      'land_without_prescription_ha'
    ),
    yieldCoveragePercentOfPrescription: getValue(
      source,
      'yieldCoveragePercentOfPrescription',
      'yield_coverage_percent_of_prescription'
    ),
    yieldPointsTotal: getValue(source, 'yieldPointsTotal', 'yield_points_total'),
    yieldPointsUsed: getValue(source, 'yieldPointsUsed', 'yield_points_used'),
  }
}

const normalizeCalculation = (calculation = {}) => {
  const source = calculation || {}
  const environments = source.environments || source.environment_results || []
  const moistureTreatment =
    getValue(source, 'moistureTreatment', 'moisture_treatment') || {}
  const normalizedEnvironments = Array.isArray(environments)
    ? environments.map(normalizeEnvironment)
    : []
  const surfaceReconciliation = normalizeSurfaceReconciliation(
    getValue(source, 'surfaceReconciliation', 'surface_reconciliation')
  )

  return {
    ...source,
    environments: sortEnvironmentsByGrossMargin(normalizedEnvironments),
    surfaceReconciliation,
    moistureTreatment: {
      ...moistureTreatment,
      inputYieldBasis: getValue(
        moistureTreatment,
        'inputYieldBasis',
        'input_yield_basis'
      ),
      originalMoistureField: getValue(
        moistureTreatment,
        'originalMoistureField',
        'original_moisture_field'
      ),
      inputCorrectionTargetPercent: getValue(
        moistureTreatment,
        'inputCorrectionTargetPercent',
        'input_correction_target_percent'
      ),
      outputCommercialTargetPercent: getValue(
        moistureTreatment,
        'outputCommercialTargetPercent',
        'output_commercial_target_percent'
      ),
      effectiveCommercialTargetPercent: getValue(
        moistureTreatment,
        'effectiveCommercialTargetPercent',
        'effective_commercial_target_percent'
      ),
      moistureFieldState: getValue(
        moistureTreatment,
        'moistureFieldState',
        'moisture_field_state'
      ),
      physicalReconstructionReliable: getValue(
        moistureTreatment,
        'physicalReconstructionReliable',
        'physical_reconstruction_reliable'
      ),
      layerPolicy: getValue(moistureTreatment, 'layerPolicy', 'layer_policy'),
      economicPolicy: getValue(
        moistureTreatment,
        'economicPolicy',
        'economic_policy'
      ),
      sourceMoistureMinPercent: getValue(
        moistureTreatment,
        'sourceMoistureMinPercent',
        'source_moisture_min_percent'
      ),
      sourceMoistureMaxPercent: getValue(
        moistureTreatment,
        'sourceMoistureMaxPercent',
        'source_moisture_max_percent'
      ),
      layerMoistureMinPercent: getValue(
        moistureTreatment,
        'layerMoistureMinPercent',
        'layer_moisture_min_percent'
      ),
      layerMoistureMaxPercent: getValue(
        moistureTreatment,
        'layerMoistureMaxPercent',
        'layer_moisture_max_percent'
      ),
      economicMoistureMinPercent: getValue(
        moistureTreatment,
        'economicMoistureMinPercent',
        'economic_moisture_min_percent'
      ),
      economicMoistureMaxPercent: getValue(
        moistureTreatment,
        'economicMoistureMaxPercent',
        'economic_moisture_max_percent'
      ),
      calibrationFactor: getValue(
        moistureTreatment,
        'calibrationFactor',
        'calibration_factor'
      ),
      harvestedTotalTonsBeforeCalibration: getValue(
        moistureTreatment,
        'harvestedTotalTonsBeforeCalibration',
        'harvested_total_tons_before_calibration'
      ),
      harvestedTotalTons: getValue(
        moistureTreatment,
        'harvestedTotalTons',
        'harvested_total_tons'
      ),
      layerTotalTons: getValue(
        moistureTreatment,
        'layerTotalTons',
        'layer_total_tons'
      ),
      economicTotalTons: getValue(
        moistureTreatment,
        'economicTotalTons',
        'economic_total_tons'
      ),
    },
    quality: normalizeQuality(source.quality),
    totals: normalizeTotals(source.totals),
    warnings: source.warnings || [],
  }
}

const getArtifacts = (report) => {
  const artifacts = report?.artifacts || {}

  return {
    ...artifacts,
    mapPng: normalizeArtifact(getValue(artifacts, 'mapPng', 'map_png')),
    pdf: normalizeArtifact(getValue(artifacts, 'pdf')),
  }
}

const getCalculation = (report) =>
  normalizeCalculation(
    getValue(report?.artifacts, 'calculation', 'calculation_json') || report?.calculation || {}
  )

const normalizeFileNames = (value) => {
  const values = Array.isArray(value) ? value : value ? [value] : []

  return values.map((item) => String(item || '').trim()).filter(Boolean)
}

const getInputSummary = (report) => {
  const provided = getValue(report, 'inputSummary', 'input_summary') || {}
  const rawPrescription = report?.prescription || {}
  const rawYieldMap = getValue(report, 'yieldMap', 'yield_map') || {}
  const rawEconomics = report?.economics || {}
  const rawReportContext = getValue(rawEconomics, 'reportContext', 'report_context') || {}
  const general = provided.general || {}
  const prescription = provided.prescription || {}
  const yieldMap = getValue(provided, 'yieldMap', 'yield_map') || {}
  const economics = provided.economics || {}
  const rawSurfaceReconciliation = getValue(
    rawEconomics,
    'surfaceReconciliation',
    'surface_reconciliation'
  )
  const surfaceReconciliation = getValue(
    economics,
    'surfaceReconciliation',
    'surface_reconciliation'
  )
  const rawEnvironmentDefinition =
    getValue(rawPrescription, 'environmentDefinition', 'environment_definition') || {}
  const rawCorrection = rawYieldMap.correction || {}
  const correction = yieldMap.correction || {}
  const rawMoisture = rawCorrection.moisture || {}
  const moisture = correction.moisture || {}
  const rawMoistureTreatment =
    getValue(rawYieldMap, 'moistureTreatment', 'moisture_treatment') || {}
  const moistureTreatment =
    getValue(yieldMap, 'moistureTreatment', 'moisture_treatment') || {}

  const normalizePrescriptionInput = (key, legacyKey = key) => {
    const normalized = getValue(prescription, key, legacyKey) || {}
    const raw = getValue(rawPrescription, key, legacyKey) || {}

    return {
      enabled: getValue(normalized, 'enabled') ?? getValue(raw, 'enabled') ?? !!getValue(raw, 'field'),
      field: getValue(normalized, 'field') ?? getValue(raw, 'field'),
      unit: getValue(normalized, 'unit') ?? getValue(raw, 'unit'),
    }
  }

  const normalizePricing = (key, legacyKey = key) => {
    const normalized = getValue(economics, key, legacyKey) || {}
    const raw = getValue(rawEconomics, key, legacyKey) || {}

    return {
      mode: getValue(normalized, 'mode') ?? getValue(raw, 'mode'),
      unit: getValue(normalized, 'unit') ?? getValue(raw, 'unit'),
      price: getValue(normalized, 'price') ?? getValue(raw, 'price'),
      seedsPerBag:
        getValue(normalized, 'seedsPerBag', 'seeds_per_bag') ??
        getValue(raw, 'seedsPerBag', 'seeds_per_bag'),
      bagPriceUsd:
        getValue(normalized, 'bagPriceUsd', 'bag_price_usd') ??
        getValue(raw, 'bagPriceUsd', 'bag_price_usd', 'price'),
    }
  }

  return {
    general: {
      reportName: getValue(general, 'reportName', 'report_name') ?? report?.name,
      campaign: general.campaign ?? report?.campaign,
      crop: general.crop ?? report?.crop,
      currency: general.currency ?? report?.currency,
      fieldId: getValue(general, 'fieldId', 'field_id') ?? getValue(report, 'fieldId', 'field_id'),
      landId: getValue(general, 'landId', 'land_id') ?? getValue(report, 'landId', 'land_id'),
      fieldName:
        getValue(general, 'fieldName', 'field_name') ??
        getValue(rawReportContext, 'fieldName', 'field_name'),
      landName:
        getValue(general, 'landName', 'land_name') ??
        getValue(rawReportContext, 'landName', 'land_name'),
      landSurfaceHa:
        getValue(general, 'landSurfaceHa', 'land_surface_ha') ??
        getValue(rawReportContext, 'landSurfaceHa', 'land_surface_ha'),
    },
    prescription: {
      fileNames: normalizeFileNames(
        getValue(prescription, 'fileNames', 'file_names') ??
          getValue(rawPrescription, 'fileNames', 'file_names')
      ),
      environmentField:
        getValue(prescription, 'environmentField', 'environment_field') ??
        getValue(rawEnvironmentDefinition, 'field'),
      seed: normalizePrescriptionInput('seed'),
      fertilizer1: normalizePrescriptionInput('fertilizer1', 'fertilizer_1'),
      fertilizer2: normalizePrescriptionInput('fertilizer2', 'fertilizer_2'),
    },
    yieldMap: {
      fileNames: normalizeFileNames(
        getValue(yieldMap, 'fileNames', 'file_names') ??
          getValue(rawYieldMap, 'fileNames', 'file_names')
      ),
      field: yieldMap.field ?? rawYieldMap.field,
      unit: yieldMap.unit ?? rawYieldMap.unit,
      correctionEnabled:
        getValue(yieldMap, 'correctionEnabled', 'correction_enabled') ??
        (hasDisplayValue(rawCorrection.enabled) ? Boolean(rawCorrection.enabled) : null),
      cleanAndCalibratedConfirmed:
        getValue(yieldMap, 'cleanAndCalibratedConfirmed', 'clean_and_calibrated_confirmed') ??
        getValue(rawYieldMap, 'cleanAndCalibratedConfirmed', 'clean_and_calibrated_confirmed'),
      commercialMoistureConfirmed:
        getValue(yieldMap, 'commercialMoistureConfirmed', 'commercial_moisture_confirmed') ??
        getValue(rawYieldMap, 'commercialMoistureConfirmed', 'commercial_moisture_confirmed'),
      correction: {
        minYieldTonsHa:
          getValue(correction, 'minYieldTonsHa', 'min_yield_tons_ha') ??
          getValue(rawCorrection, 'minYieldTonsHa', 'min_yield_tons_ha'),
        maxYieldTonsHa:
          getValue(correction, 'maxYieldTonsHa', 'max_yield_tons_ha') ??
          getValue(rawCorrection, 'maxYieldTonsHa', 'max_yield_tons_ha'),
        totalHarvestedTons:
          getValue(correction, 'totalHarvestedTons', 'total_harvested_tons') ??
          getValue(rawCorrection, 'totalHarvestedTons', 'total_harvested_tons'),
        moisture: {
          enabled: getValue(moisture, 'enabled') ?? getValue(rawMoisture, 'enabled'),
          field: getValue(moisture, 'field') ?? getValue(rawMoisture, 'field'),
          targetPercent:
            getValue(moisture, 'targetPercent', 'target_percent') ??
            getValue(rawMoisture, 'targetPercent', 'target_percent'),
        },
      },
      moistureTreatment: {
        inputYieldBasis:
          getValue(moistureTreatment, 'inputYieldBasis', 'input_yield_basis') ??
          getValue(rawMoistureTreatment, 'inputYieldBasis', 'input_yield_basis'),
        originalMoistureField:
          getValue(
            moistureTreatment,
            'originalMoistureField',
            'original_moisture_field'
          ) ??
          getValue(
            rawMoistureTreatment,
            'originalMoistureField',
            'original_moisture_field'
          ),
        inputCorrectionTargetPercent:
          getValue(
            moistureTreatment,
            'inputCorrectionTargetPercent',
            'input_correction_target_percent'
          ) ??
          getValue(
            rawMoistureTreatment,
            'inputCorrectionTargetPercent',
            'input_correction_target_percent'
          ),
        outputCommercialTargetPercent:
          getValue(
            moistureTreatment,
            'outputCommercialTargetPercent',
            'output_commercial_target_percent'
          ) ??
          getValue(
            rawMoistureTreatment,
            'outputCommercialTargetPercent',
            'output_commercial_target_percent'
          ),
        rawYieldConfirmed:
          getValue(moistureTreatment, 'rawYieldConfirmed', 'raw_yield_confirmed') ??
          getValue(rawMoistureTreatment, 'rawYieldConfirmed', 'raw_yield_confirmed'),
        originalMoistureConfirmed:
          getValue(
            moistureTreatment,
            'originalMoistureConfirmed',
            'original_moisture_confirmed'
          ) ??
          getValue(
            rawMoistureTreatment,
            'originalMoistureConfirmed',
            'original_moisture_confirmed'
          ),
      },
    },
    economics: {
      netCropPriceUsdPerTon:
        getValue(economics, 'netCropPriceUsdPerTon', 'net_crop_price_usd_per_ton') ??
        getValue(rawEconomics, 'netCropPriceUsdPerTon', 'net_crop_price_usd_per_ton'),
      seedPricing: normalizePricing('seedPricing', 'seed_pricing'),
      fertilizer1Pricing: normalizePricing('fertilizer1Pricing', 'fertilizer_1_pricing'),
      fertilizer2Pricing: normalizePricing('fertilizer2Pricing', 'fertilizer_2_pricing'),
      fixedCosts:
        getValue(economics, 'fixedCosts', 'fixed_costs') ??
        getValue(rawEconomics, 'fixedCosts', 'fixed_costs') ??
        {},
      fixedCostUsdPerHa:
        getValue(economics, 'fixedCostUsdPerHa', 'fixed_cost_usd_per_ha') ??
        getValue(rawEconomics, 'fixedCostUsdPerHa', 'fixed_cost_usd_per_ha'),
      environmentCosts:
        getValue(economics, 'environmentCosts', 'environment_costs') ??
        getValue(rawEconomics, 'environmentCosts', 'environment_costs') ??
        {},
      surfaceReconciliation:
        normalizeSurfaceReconciliation(surfaceReconciliation) ||
        normalizeSurfaceReconciliation(rawSurfaceReconciliation),
    },
  }
}

const getEnvironmentDose = (environment, inputSummary, doseField) => {
  const aliases = {
    seedDose: ['seedDose', 'seed_dose'],
    fertilizer1Dose: ['fertilizer1Dose', 'fertilizer_1_dose'],
    fertilizer2Dose: ['fertilizer2Dose', 'fertilizer_2_dose'],
  }[doseField] || [doseField]
  const calculatedValue = getNumberValue(...aliases.map((alias) => environment?.[alias]))
  if (calculatedValue !== undefined) {
    return calculatedValue
  }

  const environmentCosts = inputSummary?.economics?.environmentCosts
  if (!environmentCosts || typeof environmentCosts !== 'object') {
    return undefined
  }

  const environmentKeys = new Set(
    [environment?.environmentId, environment?.environmentName]
      .map((value) => String(value || '').trim())
      .filter(Boolean)
  )
  const configured = Object.entries(environmentCosts).find(([key]) =>
    environmentKeys.has(String(key || '').trim())
  )?.[1]

  if (!configured || typeof configured !== 'object') {
    return undefined
  }

  return getNumberValue(...aliases.map((alias) => configured[alias]))
}

const getWeightedDose = (environments, inputSummary, doseField) => {
  const values = environments
    .map((environment) => ({
      dose: getEnvironmentDose(environment, inputSummary, doseField),
      surface: getNumberValue(environment?.surfaceHa),
    }))
    .filter(({dose, surface}) => dose !== undefined && surface !== undefined && surface > 0)
  const totalSurface = values.reduce((sum, item) => sum + item.surface, 0)

  if (totalSurface <= 0) {
    return undefined
  }

  return values.reduce((sum, item) => sum + item.dose * item.surface, 0) / totalSurface
}

const formatDose = (value, unit) => {
  const numericValue = getNumberValue(value)
  if (numericValue === undefined) {
    return '-'
  }

  const unitLabel = inputUnit(unit)
  return `${formatFixedNumber(numericValue, 2)}${unitLabel ? ` ${unitLabel}` : ''}`
}

const doseHeader = (label, field) => {
  const normalizedField = String(field || '').trim()
  return normalizedField ? `${label} (${normalizedField})` : label
}

const getUsedYieldPoints = (quality = {}) =>
  getValue(
    quality,
    'yieldPointsUsed',
    'yield_points_used',
    'yieldPoints',
    'yield_points',
    'yieldPointsTotal',
    'yield_points_total',
    'totalYieldPoints',
    'total_yield_points'
  )

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

const safeDownloadName = (value) =>
  String(value || '')
    .split('')
    .filter((character) => character.charCodeAt(0) >= 32)
    .join('')
    .replace(/[<>:"/\\|?*]+/g, '-')
    .replace(/\s+/g, ' ')
    .replace(/-{2,}/g, '-')
    .replace(/^[ .-]+|[ .-]+$/g, '')

const economicArtifactFilename = (report, artifactType, t) => {
  const {general} = getInputSummary(report)
  const cropLabel = general.crop
    ? t(`reports:economic.create.inputs.crops.${general.crop}`, {
        defaultValue: general.crop,
      })
    : null
  const filenameParts = [
    report?.name || general.reportName,
    general.fieldName,
    general.landName,
    cropLabel,
    general.campaign,
  ]
    .map(safeDownloadName)
    .filter(Boolean)
  const safeReportName = filenameParts.join(' - ') || 'Reporte económico'

  if (artifactType === 'mapPng') {
    return `Mapa de margen bruto - ${safeReportName}.png`
  }

  return `${safeReportName}.pdf`
}

const DownloadButton = ({artifact, label, className, filename}) => {
  const {t} = useTranslation()
  const [downloading, setDownloading] = useState(false)

  const handleDownload = async () => {
    if (!artifact?.downloadPath || downloading) {
      return
    }

    setDownloading(true)

    try {
      const res = await ReportService.downloadEconomicReportArtifact(artifact.downloadPath)
      if (res?.ok) {
        downloadBlob(res.data, filename || artifact.filename || 'Reporte económico')
      } else {
        openAlert(
          'danger',
          t('reports:economic.create.errors.title'),
          t('reports:economic.view.download_error')
        )
      }
    } catch (error) {
      console.error(error)
      openAlert(
        'danger',
        t('reports:economic.create.errors.title'),
        t('reports:economic.view.download_error')
      )
    } finally {
      setDownloading(false)
    }
  }

  return (
    <button
      type='button'
      className={className}
      disabled={!artifact?.downloadPath || downloading}
      onClick={handleDownload}
    >
      {label}
    </button>
  )
}

const MapPreview = ({artifact}) => {
  const {t} = useTranslation()
  const [imageUrl, setImageUrl] = useState(null)
  const [failed, setFailed] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    let cancelled = false
    let objectUrl = null

    if (!artifact?.downloadPath) {
      setImageUrl(null)
      setFailed(false)
      setLoading(false)
      return () => {}
    }

    setImageUrl(null)
    setFailed(false)
    setLoading(true)

    ReportService.downloadEconomicReportArtifact(artifact.downloadPath)
      .then((res) => {
        if (cancelled) {
          return
        }

        if (res?.ok) {
          objectUrl = URL.createObjectURL(res.data)
          setImageUrl(objectUrl)
        } else {
          setFailed(true)
        }
      })
      .catch((error) => {
        console.error(error)
        if (!cancelled) {
          setFailed(true)
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })

    return () => {
      cancelled = true
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl)
      }
    }
  }, [artifact?.downloadPath])

  return (
    <div className='economic-report-map-preview'>
      {imageUrl && !failed ? (
        <img src={imageUrl} alt={t('reports:economic.view.sections.map')} />
      ) : failed ? (
        <div className='economic-report-map-preview__error'>
          {t('reports:economic.view.download_error')}
        </div>
      ) : loading ? (
        <Loading />
      ) : (
        <div className='economic-report-map-preview__error'>{t('reports:economic.view.empty')}</div>
      )}
    </div>
  )
}

const InputSummaryGroup = ({title, rows, wide = false}) => {
  const visibleRows = rows.filter((row) => hasDisplayValue(row.value))

  if (visibleRows.length < 1) {
    return null
  }

  return (
    <article
      className={`economic-report-input-group${
        wide ? ' economic-report-input-group--wide' : ''
      }`}
    >
      <h6 className='economic-report-input-group__title'>{title}</h6>
      <div className='economic-report-input-group__rows'>
        {visibleRows.map((row) => (
          <div key={row.label} className='economic-report-input-row'>
            <span>{row.label}</span>
            <strong>{row.value}</strong>
          </div>
        ))}
      </div>
    </article>
  )
}

const ReportSummaryContext = ({report}) => {
  const {t} = useTranslation()
  const {general} = getInputSummary(report)
  const cropLabel = general.crop
    ? t(`reports:economic.create.inputs.crops.${general.crop}`, {
        defaultValue: general.crop,
      })
    : null
  const values = [general.fieldName, general.landName, cropLabel, general.campaign].filter(
    hasDisplayValue
  )

  if (values.length < 1) {
    return null
  }

  return <p className='economic-report-summary-context'>{values.join(' - ')}</p>
}

const ReportInputSummary = ({report}) => {
  const {t} = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const input = getInputSummary(report)
  const {prescription, yieldMap, economics} = input
  const calculation = getCalculation(report)
  const declaredSurfaceReconciliation = economics.surfaceReconciliation
  const calculatedSurfaceReconciliation = calculation.surfaceReconciliation
  const surfaceReconciliation = mergeSurfaceReconciliationForDisplay(
    declaredSurfaceReconciliation,
    calculatedSurfaceReconciliation
  )
  const calculatedMoistureTreatment = calculation.moistureTreatment || {}
  const moistureTreatment = yieldMap.moistureTreatment || {}
  const inputYieldBasis =
    moistureTreatment.inputYieldBasis || calculatedMoistureTreatment.inputYieldBasis
  const isMoistureV2 =
    Number(calculation.version) === 2 ||
    MOISTURE_V2_INPUT_YIELD_BASES.has(inputYieldBasis)
  const files = (values) => (values.length > 0 ? values.join(', ') : null)
  const yesNo = (value) => {
    if (value === true) {
      return t('reports:economic.view.values.yes')
    }

    if (value === false) {
      return t('reports:economic.view.values.no')
    }

    return null
  }
  const correctionApplied = hasDisplayValue(yieldMap.correctionEnabled)
    ? yieldMap.correctionEnabled
      ? t('reports:economic.view.values.correction_applied')
      : t('reports:economic.view.values.correction_not_applied')
    : null
  const yieldReady =
    isMoistureV2 && yieldMap.cleanAndCalibratedConfirmed === true
      ? true
      : !isMoistureV2 &&
        yieldMap.cleanAndCalibratedConfirmed === true &&
        yieldMap.commercialMoistureConfirmed === true
      ? true
      : yieldMap.cleanAndCalibratedConfirmed === false ||
        yieldMap.commercialMoistureConfirmed === false
      ? false
      : null
  const moistureBasisLabel = inputYieldBasis
    ? t(`reports:economic.create.moisture_basis.${inputYieldBasis}.label`, {
        defaultValue: inputYieldBasis,
      })
    : t('reports:economic.create.moisture_basis.legacy_shared_unknown.label')
  const fertilizerPrice = (pricing, prescriptionInput) => {
    if (!hasDisplayValue(pricing?.price)) {
      return null
    }

    const suffix = pricing.unit === 'usd_per_liter' || prescriptionInput?.unit === 'l_per_ha'
      ? ' USD/l'
      : ' USD/t'
    return formatInputNumber(pricing.price, suffix)
  }
  const fixedCostDefinitions = [
    ['sowing', 'fixed_cost_sowing'],
    ['spraying', 'fixed_cost_spraying'],
    ['herbicides', 'fixed_cost_herbicides'],
    ['harvest', 'fixed_cost_harvest'],
    ['rent', 'fixed_cost_rent'],
    ['agronomist', 'fixed_cost_agronomist'],
    ['administration', 'fixed_cost_administration'],
  ]
  const itemizedFixedCosts = fixedCostDefinitions
    .map(([key, translationKey]) => ({
      label: t(`reports:economic.view.fields.${translationKey}`),
      value: formatInputNumber(economics.fixedCosts?.[key], ' USD/ha'),
    }))
    .filter((row) => hasDisplayValue(row.value))
  const hasItemizedFixedCosts = itemizedFixedCosts.length > 0
  const fixedCostTotal = hasItemizedFixedCosts
    ? null
    : economics.fixedCostUsdPerHa
  const surfaceStatus = (status) =>
    status
      ? t(`reports:economic.view.values.surface_status.${status}`, {
          defaultValue: status,
        })
      : null
  const surfaceReconciliationGroup = surfaceReconciliation
    ? {
        title: t('reports:economic.view.sections.surface_reconciliation'),
        wide: true,
        rows: [
          {
            label: t('reports:economic.view.fields.sowing_declaration'),
            value: surfaceStatus(surfaceReconciliation.sowingStatus),
          },
          {
            label: t('reports:economic.view.fields.declared_sown_surface'),
            value:
              surfaceReconciliation.sowingStatus === 'differs'
                ? formatInputNumber(
                    surfaceReconciliation.declaredSownSurfaceHa,
                    ' ha'
                  )
                : null,
          },
          {
            label: t('reports:economic.view.fields.harvest_declaration'),
            value: surfaceStatus(surfaceReconciliation.harvestStatus),
          },
          {
            label: t(
              'reports:economic.view.fields.declared_harvested_surface'
            ),
            value:
              surfaceReconciliation.harvestStatus === 'differs'
                ? formatInputNumber(
                    surfaceReconciliation.declaredHarvestedSurfaceHa,
                    ' ha'
                  )
                : null,
          },
          {
            label: t(
              'reports:economic.view.fields.yield_map_coverage_declaration'
            ),
            value: surfaceStatus(
              surfaceReconciliation.yieldMapCoverageStatus
            ),
          },
        ],
      }
    : null
  const groups = [
    {
      title: t('reports:economic.view.sections.prescription_data'),
      rows: [
        {
          label: t('reports:economic.view.fields.uploaded_files'),
          value: files(prescription.fileNames),
        },
        {
          label: t('reports:economic.view.fields.environment_field'),
          value: prescription.environmentField,
        },
        {label: t('reports:economic.view.fields.seed_field'), value: prescription.seed.field},
        {label: t('reports:economic.view.fields.seed_unit'), value: inputUnit(prescription.seed.unit)},
        {
          label: t('reports:economic.view.fields.fertilizer_1_field'),
          value: prescription.fertilizer1.enabled ? prescription.fertilizer1.field : null,
        },
        {
          label: t('reports:economic.view.fields.fertilizer_1_unit'),
          value: prescription.fertilizer1.enabled ? inputUnit(prescription.fertilizer1.unit) : null,
        },
        {
          label: t('reports:economic.view.fields.fertilizer_2_field'),
          value: prescription.fertilizer2.enabled ? prescription.fertilizer2.field : null,
        },
        {
          label: t('reports:economic.view.fields.fertilizer_2_unit'),
          value: prescription.fertilizer2.enabled ? inputUnit(prescription.fertilizer2.unit) : null,
        },
      ],
    },
    {
      title: t('reports:economic.view.sections.yield_data'),
      rows: [
        {
          label: t('reports:economic.view.fields.uploaded_files'),
          value: files(yieldMap.fileNames),
        },
        {label: t('reports:economic.view.fields.yield_field'), value: yieldMap.field},
        {label: t('reports:economic.view.fields.yield_unit'), value: inputUnit(yieldMap.unit)},
        {
          label: t('reports:economic.view.fields.input_yield_basis'),
          value: moistureBasisLabel,
        },
        {
          label: t('reports:economic.view.fields.correction_mode'),
          value: correctionApplied,
        },
        {
          label: t('reports:economic.view.fields.yield_ready_confirmation'),
          value: yieldMap.correctionEnabled ? null : yesNo(yieldReady),
        },
        {
          label: t('reports:economic.view.fields.configured_min_yield'),
          value: yieldMap.correctionEnabled
            ? formatInputNumber(yieldMap.correction.minYieldTonsHa, ' t/ha')
            : null,
        },
        {
          label: t('reports:economic.view.fields.configured_max_yield'),
          value: yieldMap.correctionEnabled
            ? formatInputNumber(yieldMap.correction.maxYieldTonsHa, ' t/ha')
            : null,
        },
        {
          label: t('reports:economic.view.fields.harvested_total_tons'),
          value: formatInputNumber(
            yieldMap.correction.totalHarvestedTons,
            ' t'
          ),
        },
        {
          label: t('reports:economic.view.fields.moisture_field'),
          value: isMoistureV2
            ? moistureTreatment.originalMoistureField ||
              calculatedMoistureTreatment.originalMoistureField
            : yieldMap.correction.moisture?.enabled
            ? yieldMap.correction.moisture.field
            : null,
        },
        {
          label: t('reports:economic.view.fields.target_moisture'),
          value: isMoistureV2
            ? formatInputNumber(
                moistureTreatment.outputCommercialTargetPercent ??
                  calculatedMoistureTreatment.outputCommercialTargetPercent,
                ' %'
              )
            : yieldMap.correction.moisture?.enabled
            ? formatInputNumber(yieldMap.correction.moisture.targetPercent, ' %')
            : null,
        },
      ],
    },
    ...(surfaceReconciliationGroup ? [surfaceReconciliationGroup] : []),
    {
      title: t('reports:economic.view.sections.income_data'),
      rows: [
        {
          label: t('reports:economic.view.fields.net_crop_price'),
          value: formatInputNumber(economics.netCropPriceUsdPerTon, ' USD/t'),
        },
      ],
    },
    {
      title: t('reports:economic.view.sections.other_data'),
      rows: [
        {
          label: t('reports:economic.view.fields.seeds_per_bag'),
          value: formatInputNumber(economics.seedPricing.seedsPerBag, '', 0),
        },
      ],
    },
    {
      title: t('reports:economic.view.sections.variable_cost_data'),
      rows: [
        {
          label: t('reports:economic.view.fields.seed_bag_price'),
          value: formatInputNumber(economics.seedPricing.bagPriceUsd, ' USD'),
        },
        {
          label: t('reports:economic.view.fields.fertilizer_1_price'),
          value: prescription.fertilizer1.enabled
            ? fertilizerPrice(economics.fertilizer1Pricing, prescription.fertilizer1)
            : null,
        },
        {
          label: t('reports:economic.view.fields.fertilizer_2_price'),
          value: prescription.fertilizer2.enabled
            ? fertilizerPrice(economics.fertilizer2Pricing, prescription.fertilizer2)
            : null,
        },
      ],
    },
    {
      title: t('reports:economic.view.sections.fixed_cost_data'),
      rows: [
        ...itemizedFixedCosts,
        {
          label: t('reports:economic.view.fields.fixed_cost_total'),
          value: formatInputNumber(fixedCostTotal, ' USD/ha'),
        },
      ],
    },
  ]

  return (
    <section className='economic-report-view-section'>
      <h6 className='economic-report-collapsible-heading'>
        <button
          type='button'
          className={`economic-report-section-toggle${expanded ? ' is-open' : ''}`}
          aria-expanded={expanded}
          aria-controls='economic-report-input-summary-content'
          onClick={() => setExpanded((current) => !current)}
        >
          <span>{t('reports:economic.view.sections.inputs')}</span>
          <span className='economic-report-section-toggle__chevron' aria-hidden='true' />
        </button>
      </h6>
      {expanded && (
        <div id='economic-report-input-summary-content' className='economic-report-input-grid'>
          {groups.map((group) => (
            <InputSummaryGroup
              key={group.title}
              title={group.title}
              rows={group.rows}
              wide={group.wide}
            />
          ))}
        </div>
      )}
    </section>
  )
}

const SummaryCardGrid = ({items}) => (
  <div className='economic-report-summary-grid'>
    {items.map((item) => (
      <div key={item.label} className='economic-report-summary-card'>
        <span>{item.label}</span>
        <strong>{item.value}</strong>
      </div>
    ))}
  </div>
)

const SummarySections = ({report, totals}) => {
  const {t} = useTranslation()
  const hasSeparatedYieldSeries =
    hasDisplayValue(totals.layerYieldTonsHa) ||
    hasDisplayValue(totals.economicYieldTonsHa) ||
    hasDisplayValue(totals.harvestedYieldTonsHa)
  const productiveItems = [
    {label: t('reports:economic.view.fields.surface'), value: hectares(totals.surfaceHa)},
    ...(hasSeparatedYieldSeries
      ? [
          {
            label: t('reports:economic.view.fields.harvested_yield'),
            value: tonsHa(totals.harvestedYieldTonsHa),
          },
          {
            label: t('reports:economic.view.fields.layer_yield'),
            value: tonsHa(totals.layerYieldTonsHa),
          },
          {
            label: t('reports:economic.view.fields.economic_yield'),
            value: tonsHa(totals.economicYieldTonsHa ?? totals.yieldTonsHa),
          },
          {
            label: t('reports:economic.view.fields.harvested_total_tons'),
            value: numberOrDash(totals.harvestedTotalTons, 2, ' t'),
          },
          {
            label: t('reports:economic.view.fields.layer_total_tons'),
            value: numberOrDash(totals.layerTotalTons, 2, ' t'),
          },
          {
            label: t('reports:economic.view.fields.economic_total_tons'),
            value: numberOrDash(totals.economicTotalTons, 2, ' t'),
          },
        ]
      : [
          {
            label: t('reports:economic.view.fields.yield_weighted'),
            value: tonsHa(totals.yieldTonsHa),
          },
        ]),
  ]
  const economicItems = [
    {label: t('reports:economic.view.fields.revenue_weighted'), value: usdHa(totals.revenueUsdHa)},
    {
      label: t('reports:economic.view.fields.variable_cost_weighted'),
      value: usdHa(totals.variableCostUsdHa),
    },
    {
      label: t('reports:economic.view.fields.fixed_cost_weighted'),
      value: usdHa(totals.fixedCostUsdHa),
    },
    {label: t('reports:economic.view.fields.total_cost_weighted'), value: usdHa(totals.totalCostUsdHa)},
    {
      label: t('reports:economic.view.fields.gross_margin_weighted'),
      value: usdHa(totals.grossMarginUsdHa),
    },
    {
      label: t('reports:economic.view.fields.gross_margin_total'),
      value: currency(totals.grossMarginUsdTotal),
    },
  ]

  return (
    <>
      <section className='economic-report-view-section'>
        <div className='economic-report-section-heading'>
          <h6>{t('reports:economic.view.sections.productive_summary')}</h6>
          <ReportSummaryContext report={report} />
        </div>
        <SummaryCardGrid items={productiveItems} />
        {hasSeparatedYieldSeries && (
          <div className='economic-report-productive-note'>
            <p>{t('reports:economic.view.productive_summary_note')}</p>
            <p>{t('reports:economic.view.productive_summary_weighted_note')}</p>
          </div>
        )}
      </section>
      <section className='economic-report-view-section'>
        <h6>{t('reports:economic.view.sections.summary')}</h6>
        <SummaryCardGrid items={economicItems} />
      </section>
    </>
  )
}

const EnvironmentsTable = ({environments, totals, quality, report}) => {
  const {t} = useTranslation()

  if (!Array.isArray(environments) || environments.length < 1) {
    return null
  }

  const inputSummary = getInputSummary(report)
  const prescription = inputSummary.prescription
  const totalSeedDose =
    getNumberValue(totals?.seedDose) ??
    getWeightedDose(environments, inputSummary, 'seedDose')
  const totalFertilizer1Dose =
    getNumberValue(totals?.fertilizer1Dose) ??
    getWeightedDose(environments, inputSummary, 'fertilizer1Dose')
  const totalFertilizer2Dose =
    getNumberValue(totals?.fertilizer2Dose) ??
    getWeightedDose(environments, inputSummary, 'fertilizer2Dose')
  const hasSeparatedYieldSeries = environments.some((environment) =>
    hasDisplayValue(environment.economicYieldTonsHa)
  )

  return (
    <section className='economic-report-view-section'>
      <h6>{t('reports:economic.view.sections.environments')}</h6>
      <div className='economic-report-table-shell'>
        <table className='economic-report-table'>
          <thead>
            <tr>
              <th>{t('reports:economic.view.fields.environment')}</th>
              <th>{t('reports:economic.view.fields.surface')}</th>
              <th>{t('reports:economic.view.fields.points_short')}</th>
              <th>{t('reports:economic.view.fields.seed_dose')}</th>
              <th>
                {doseHeader(
                  t('reports:economic.view.fields.fertilizer_1_dose'),
                  prescription.fertilizer1.field
                )}
              </th>
              <th>
                {doseHeader(
                  t('reports:economic.view.fields.fertilizer_2_dose'),
                  prescription.fertilizer2.field
                )}
              </th>
              <th>{t('reports:economic.view.fields.yield')}</th>
              <th>{t('reports:economic.view.fields.revenue')}</th>
              <th>{t('reports:economic.view.fields.variable_cost')}</th>
              <th>{t('reports:economic.view.fields.fixed_cost')}</th>
              <th>{t('reports:economic.view.fields.total_cost')}</th>
              <th>{t('reports:economic.view.fields.gross_margin')}</th>
              <th>{t('reports:economic.view.fields.gross_margin_total')}</th>
            </tr>
          </thead>
          <tbody>
            {environments.map((environment) => (
              <tr key={environment.environmentId}>
                <td>{environment.environmentName || environment.environmentId}</td>
                <td>{hectares(environment.surfaceHa)}</td>
                <td>{formatFixedNumber(environment.pointCount, 0)}</td>
                <td>
                  {formatDose(
                    getEnvironmentDose(environment, inputSummary, 'seedDose'),
                    prescription.seed.unit
                  )}
                </td>
                <td>
                  {formatDose(
                    getEnvironmentDose(environment, inputSummary, 'fertilizer1Dose'),
                    prescription.fertilizer1.unit
                  )}
                </td>
                <td>
                  {formatDose(
                    getEnvironmentDose(environment, inputSummary, 'fertilizer2Dose'),
                    prescription.fertilizer2.unit
                  )}
                </td>
                <td>
                  {tonsHa(environment.economicYieldTonsHa ?? environment.yieldTonsHa)}
                </td>
                <td>{usdHa(environment.revenueUsdHa)}</td>
                <td>{usdHa(environment.variableCostUsdHa)}</td>
                <td>{usdHa(environment.fixedCostUsdHa)}</td>
                <td>{usdHa(environment.totalCostUsdHa)}</td>
                <td>{usdHa(environment.grossMarginUsdHa)}</td>
                <td>{currency(environment.grossMarginUsdTotal)}</td>
              </tr>
            ))}
            <tr className='economic-report-table-total-row'>
              <td>{t('reports:economic.view.fields.total_weighted')}</td>
              <td>{hectares(totals?.surfaceHa)}</td>
              <td>{formatFixedNumber(getUsedYieldPoints(quality), 0)}</td>
              <td>{formatDose(totalSeedDose, prescription.seed.unit)}</td>
              <td>{formatDose(totalFertilizer1Dose, prescription.fertilizer1.unit)}</td>
              <td>{formatDose(totalFertilizer2Dose, prescription.fertilizer2.unit)}</td>
              <td>{tonsHa(totals?.economicYieldTonsHa ?? totals?.yieldTonsHa)}</td>
              <td>{usdHa(totals?.revenueUsdHa)}</td>
              <td>{usdHa(totals?.variableCostUsdHa)}</td>
              <td>{usdHa(totals?.fixedCostUsdHa)}</td>
              <td>{usdHa(totals?.totalCostUsdHa)}</td>
              <td>{usdHa(totals?.grossMarginUsdHa)}</td>
              <td>{currency(totals?.grossMarginUsdTotal)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className='economic-report-table-notes'>
        <span className='economic-report-table-note'>
          {t('reports:economic.view.fields.total_weighted_note', {
            points: formatFixedNumber(getUsedYieldPoints(quality), 0),
            total: currency(totals?.grossMarginUsdTotal),
          })}
        </span>
        {hasSeparatedYieldSeries && (
          <span className='economic-report-table-note'>
            {t('reports:economic.view.fields.economic_yield_note')}
          </span>
        )}
        <span className='economic-report-table-note'>
          {t('reports:economic.view.fields.gross_margin_formula')}
        </span>
      </div>
    </section>
  )
}

const QualitySummary = ({calculation}) => {
  const {t} = useTranslation()
  const quality = calculation?.quality
  const calculationVersion = calculation?.version
  const totals = calculation?.totals || {}

  if (!quality) {
    return null
  }

  const items = [
    {
      label: t('reports:economic.view.fields.points'),
      value: getUsedYieldPoints(quality),
    },
    {
      label: t('reports:economic.view.fields.points_outside_land'),
      value: quality.yieldPointsOutsideLand,
    },
    {
      label: t('reports:economic.view.fields.points_outside_prescription'),
      value: quality.yieldPointsOutsidePrescription,
    },
    {
      label: t('reports:economic.view.fields.removed_yield_points'),
      value:
        (quality.yieldPointsRemovedByMin || 0) + (quality.yieldPointsRemovedByMax || 0),
    },
    {
      label: t('reports:economic.view.fields.polygons_without_yield'),
      value: Array.isArray(quality.polygonsWithoutYield)
        ? quality.polygonsWithoutYield.length
        : quality.polygonsWithoutYield,
    },
    {
      label: t('reports:economic.view.fields.land_surface'),
      displayValue: numberOrDash(quality.landSurfaceHa, 2, ' ha'),
    },
    {
      label: t('reports:economic.view.fields.prescription_surface'),
      displayValue: numberOrDash(quality.prescriptionSurfaceHa, 2, ' ha'),
    },
    {
      label: t('reports:economic.view.fields.analyzed_surface'),
      displayValue: surfaceCoverageOrDash(
        quality.surfaceWithYieldHa,
        quality.yieldCoveragePercentOfPrescription
      ),
      note: t('reports:economic.view.fields.analyzed_surface_basis'),
    },
    {
      label: t('reports:economic.view.fields.land_without_prescription'),
      displayValue: numberOrDash(quality.landWithoutPrescriptionHa, 2, ' ha'),
    },
    {
      label: t('reports:economic.view.fields.prescription_without_yield'),
      displayValue: numberOrDash(quality.surfaceWithoutYieldHa, 2, ' ha'),
    },
    {
      label: t('reports:economic.view.fields.prescription_outside_land'),
      displayValue: numberOrDash(quality.prescriptionSurfaceOutsideLandHa, 2, ' ha'),
    },
  ]
  const correction = quality.yieldCorrection || {}
  const getCorrectionValue = (...keys) => getValue(correction, ...keys)
  const rawMinTonsHa = getNumberValue(getCorrectionValue('rawMinTonsHa', 'raw_min_tons_ha'))
  const rawMaxTonsHa = getNumberValue(getCorrectionValue('rawMaxTonsHa', 'raw_max_tons_ha'))
  const cleanedMinTonsHa = getNumberValue(
    getCorrectionValue('cleanedMinTonsHa', 'cleaned_min_tons_ha')
  )
  const cleanedMaxTonsHa = getNumberValue(
    getCorrectionValue('cleanedMaxTonsHa', 'cleaned_max_tons_ha')
  )
  const totalHarvestedTonsFactor = getNumberValue(
    getCorrectionValue('totalHarvestedTonsFactor', 'total_harvested_tons_factor', 'tonFactor')
  )
  const totalHarvestedTonsAfterCalibration = getNumberValue(
    getCorrectionValue(
      'totalHarvestedTonsAfterCalibration',
      'total_harvested_tons_after_calibration',
      'totalHarvestedTonsCorrected',
      'total_harvested_tons_corrected'
    )
  )
  const totalHarvestedTonsBeforeCalibration = getNumberValue(
    getCorrectionValue(
      'totalHarvestedTonsBeforeCalibration',
      'total_harvested_tons_before_calibration',
      'totalHarvestedTonsBeforeCorrection',
      'total_harvested_tons_before_correction'
    )
  )
  const totalHarvestedTonsCommercialMoisture = getNumberValue(
    getCorrectionValue(
      'totalHarvestedTonsCommercialMoisture',
      'total_harvested_tons_commercial_moisture'
    )
  )
  const sourceMoistureMinPercent = getNumberValue(
    getCorrectionValue(
      'sourceMoistureMinPercent',
      'source_moisture_min_percent',
      'sourceMoistureMin',
      'source_moisture_min',
      'rawMoistureMinPercent',
      'raw_moisture_min_percent',
      'moistureMinPercent',
      'moisture_min_percent'
    )
  )
  const sourceMoistureMaxPercent = getNumberValue(
    getCorrectionValue(
      'sourceMoistureMaxPercent',
      'source_moisture_max_percent',
      'sourceMoistureMax',
      'source_moisture_max',
      'rawMoistureMaxPercent',
      'raw_moisture_max_percent',
      'moistureMaxPercent',
      'moisture_max_percent'
    )
  )
  const correctedMoistureMinPercent = getNumberValue(
    getCorrectionValue(
      'correctedMoistureMinPercent',
      'corrected_moisture_min_percent',
      'targetMoistureMinPercent',
      'target_moisture_min_percent'
    )
  )
  const correctedMoistureMaxPercent = getNumberValue(
    getCorrectionValue(
      'correctedMoistureMaxPercent',
      'corrected_moisture_max_percent',
      'targetMoistureMaxPercent',
      'target_moisture_max_percent'
    )
  )
  const usesLegacyCommercialMoistureBasis =
    getCorrectionValue('totalHarvestedTonsBasis', 'total_harvested_tons_basis') ===
    'commercial_moisture'
  const separatedMoistureTreatment = correction.moistureTreatment || {}
  const hasSeparatedMoistureTreatment =
    Number(calculationVersion) === 2 ||
    MOISTURE_V2_INPUT_YIELD_BASES.has(
      separatedMoistureTreatment.inputYieldBasis
    )
  const legacyCorrectionGroups = correction.enabled && !hasSeparatedMoistureTreatment
    ? [
        {
          title: t('reports:economic.view.fields.extreme_cleaning'),
          rows: [
            {
              label: t('reports:economic.view.fields.yield_range_before_correction'),
              value: rangeOrDash(rawMinTonsHa, rawMaxTonsHa, 2, ' t/ha'),
            },
            {
              label: t('reports:economic.view.fields.yield_range_after_cleaning'),
              value: rangeOrDash(cleanedMinTonsHa, cleanedMaxTonsHa, 2, ' t/ha'),
            },
          ],
        },
        ...(usesLegacyCommercialMoistureBasis
          ? [
              {
                title: t('reports:economic.view.fields.commercial_moisture_correction'),
                rows: [
                  {
                    label: t('reports:economic.view.fields.moisture_range_before_correction'),
                    value: rangeOrDash(
                      sourceMoistureMinPercent,
                      sourceMoistureMaxPercent,
                      2,
                      ' %'
                    ),
                  },
                  {
                    label: t('reports:economic.view.fields.moisture_range_corrected'),
                    value: rangeOrDash(
                      correctedMoistureMinPercent,
                      correctedMoistureMaxPercent,
                      2,
                      ' %'
                    ),
                  },
                ],
              },
              {
                title: t('reports:economic.view.fields.legacy_total_harvest_calibration'),
                rows: [
                  {
                    label: t(
                      'reports:economic.view.fields.legacy_total_tons_before_calibration'
                    ),
                    value: numberOrDash(totalHarvestedTonsBeforeCalibration, 2, ' t'),
                  },
                  {
                    label: t('reports:economic.view.fields.legacy_total_harvested_tons'),
                    value: numberOrDash(totalHarvestedTonsAfterCalibration, 2, ' t'),
                  },
                  {
                    label: t('reports:economic.view.fields.calibration_factor'),
                    value: numberOrDash(totalHarvestedTonsFactor),
                  },
                ],
              },
            ]
          : [
              {
                title: t('reports:economic.view.fields.total_harvest_calibration'),
                rows: [
                  {
                    label: t('reports:economic.view.fields.total_tons_before_calibration'),
                    value: numberOrDash(totalHarvestedTonsBeforeCalibration, 2, ' t'),
                  },
                  {
                    label: t('reports:economic.view.fields.total_harvested_tons'),
                    value: numberOrDash(totalHarvestedTonsAfterCalibration, 2, ' t'),
                  },
                  {
                    label: t('reports:economic.view.fields.calibration_factor'),
                    value: numberOrDash(totalHarvestedTonsFactor),
                  },
                ],
              },
              {
                title: t('reports:economic.view.fields.commercial_moisture_correction'),
                rows: [
                  {
                    label: t('reports:economic.view.fields.moisture_range_before_correction'),
                    value: rangeOrDash(
                      sourceMoistureMinPercent,
                      sourceMoistureMaxPercent,
                      2,
                      ' %'
                    ),
                  },
                  {
                    label: t('reports:economic.view.fields.moisture_range_corrected'),
                    value: rangeOrDash(
                      correctedMoistureMinPercent,
                      correctedMoistureMaxPercent,
                      2,
                      ' %'
                    ),
                  },
                  {
                    label: t('reports:economic.view.fields.total_tons_commercial_moisture'),
                    value: numberOrDash(totalHarvestedTonsCommercialMoisture, 2, ' t'),
                  },
                ],
              },
            ]),
        {
          title: t('reports:economic.view.fields.final_correction_result'),
          rows: [
            {
              label: t('reports:economic.view.fields.final_corrected_yield_range'),
              value: rangeOrDash(
                getCorrectionValue('correctedMinTonsHa', 'corrected_min_tons_ha'),
                getCorrectionValue('correctedMaxTonsHa', 'corrected_max_tons_ha'),
                2,
                ' t/ha'
              ),
            },
          ],
        },
      ]
    : []
  const separatedCorrectionGroups = hasSeparatedMoistureTreatment
    ? [
        {
          title: t('reports:economic.view.fields.extreme_cleaning'),
          rows: [
            {
              label: t('reports:economic.view.fields.yield_range_before_correction'),
              value: rangeOrDash(rawMinTonsHa, rawMaxTonsHa, 2, ' t/ha'),
            },
            {
              label: t('reports:economic.view.fields.yield_range_after_cleaning'),
              value: rangeOrDash(cleanedMinTonsHa, cleanedMaxTonsHa, 2, ' t/ha'),
            },
          ],
        },
        {
          title: t('reports:economic.view.fields.total_harvest_calibration'),
          rows: [
            {
              label: t('reports:economic.view.fields.total_tons_before_calibration'),
              value: numberOrDash(
                separatedMoistureTreatment.harvestedTotalTonsBeforeCalibration,
                2,
                ' t'
              ),
            },
            {
              label: t('reports:economic.view.fields.total_harvested_tons'),
              value: numberOrDash(
                separatedMoistureTreatment.harvestedTotalTons,
                2,
                ' t'
              ),
            },
            {
              label: t('reports:economic.view.fields.calibration_factor'),
              value: numberOrDash(separatedMoistureTreatment.calibrationFactor),
            },
          ],
        },
        {
          title: t('reports:economic.view.fields.commercial_moisture_correction'),
          rows: [
            {
              label: t('reports:economic.view.fields.moisture_range_before_correction'),
              value: rangeOrDash(
                separatedMoistureTreatment.sourceMoistureMinPercent,
                separatedMoistureTreatment.sourceMoistureMaxPercent,
                2,
                ' %'
              ),
            },
            {
              label: t('reports:economic.view.fields.moisture_range_layer'),
              value: rangeOrDash(
                separatedMoistureTreatment.layerMoistureMinPercent,
                separatedMoistureTreatment.layerMoistureMaxPercent,
                2,
                ' %'
              ),
            },
            {
              label: t('reports:economic.view.fields.moisture_range_economic'),
              value: rangeOrDash(
                separatedMoistureTreatment.economicMoistureMinPercent,
                separatedMoistureTreatment.economicMoistureMaxPercent,
                2,
                ' %'
              ),
            },
          ],
        },
        {
          title: t('reports:economic.view.fields.final_correction_result'),
          rows: [
            {
              label: t('reports:economic.view.fields.harvested_yield'),
              value: tonsHa(totals.harvestedYieldTonsHa),
            },
            {
              label: t('reports:economic.view.fields.layer_yield'),
              value: tonsHa(totals.layerYieldTonsHa),
            },
            {
              label: t('reports:economic.view.fields.economic_yield'),
              value: tonsHa(totals.economicYieldTonsHa),
            },
            {
              label: t('reports:economic.view.fields.harvested_total_tons'),
              value: numberOrDash(totals.harvestedTotalTons, 2, ' t'),
            },
            {
              label: t('reports:economic.view.fields.layer_total_tons'),
              value: numberOrDash(totals.layerTotalTons, 2, ' t'),
            },
            {
              label: t('reports:economic.view.fields.economic_total_tons'),
              value: numberOrDash(totals.economicTotalTons, 2, ' t'),
            },
          ],
        },
      ]
    : []
  const correctionGroups = [
    ...legacyCorrectionGroups,
    ...separatedCorrectionGroups,
  ]

  return (
    <section className='economic-report-view-section'>
      <h6>{t('reports:economic.view.sections.quality')}</h6>
      <div className='economic-report-quality-grid'>
        {items.map((item) => (
          <div key={item.label} className='economic-report-quality-item'>
            <span>{item.label}</span>
            <strong>{item.displayValue || integer(item.value)}</strong>
            {item.note && <small>{item.note}</small>}
          </div>
        ))}
      </div>
      {correctionGroups.length > 0 && (
        <div className='economic-report-correction-summary'>
          {correctionGroups.map((group) => (
            <div key={group.title} className='economic-report-correction-group'>
              <h6>{group.title}</h6>
              <div className='economic-report-correction-group__rows'>
                {group.rows.map((row) => (
                  <div key={row.label} className='economic-report-correction-row'>
                    <span>{row.label}</span>
                    <strong>{row.value}</strong>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

const Warnings = ({warnings}) => {
  const {t} = useTranslation()

  if (!Array.isArray(warnings) || warnings.length < 1) {
    return null
  }

  return (
    <section className='economic-report-view-section'>
      <h6>{t('reports:economic.view.sections.warnings')}</h6>
      <div className='economic-report-warnings'>
        {warnings.map((warning) => (
          <span key={warning}>{warning}</span>
        ))}
      </div>
    </section>
  )
}

const SurfaceReconciliationConsistencyWarning = ({issues}) => {
  const {t} = useTranslation()

  if (!issues.length) {
    return null
  }

  return (
    <section className='economic-report-view-section'>
      <h6>
        {t('reports:economic.view.sections.surface_consistency_warning')}
      </h6>
      <div className='economic-report-warnings economic-report-surface-consistency-warning'>
        {issues.map((issue) => (
          <span key={issue}>
            {t(`reports:economic.view.values.surface_issue.${issue}`, {
              defaultValue: t(
                'reports:economic.view.values.surface_issue.unknown'
              ),
            })}
          </span>
        ))}
      </div>
    </section>
  )
}

const ReportBody = ({mobile = false}) => {
  const {t} = useTranslation()
  const {report} = useViewEconomicReportContext()
  const artifacts = getArtifacts(report)
  const calculation = getCalculation(report)
  const totals = calculation.totals || {}
  const map = artifacts.mapPng
  const declaredSurfaceReconciliation = getInputSummary(report).economics
    .surfaceReconciliation
  const surfaceConsistencyIssues = getSurfaceReconciliationConsistencyIssues(
    declaredSurfaceReconciliation,
    calculation.surfaceReconciliation
  )
  const warnings = filterDuplicateSurfaceReconciliationWarnings(
    report.warnings || calculation.warnings,
    surfaceConsistencyIssues.length > 0
  )

  if (!calculation.totals || Object.keys(calculation.totals).length < 1) {
    return (
      <div className={mobile ? '' : 'modal-body'}>
        <p className='m-0 reports-empty-state'>{t('reports:economic.view.empty')}</p>
      </div>
    )
  }

  return (
    <div
      className={
        mobile
          ? 'economic-report-view economic-report-view--mobile'
          : 'modal-body economic-report-view'
      }
    >
      <SummarySections report={report} totals={totals} />
      {map?.downloadPath && (
        <section className='economic-report-view-section'>
          <h6>{t('reports:economic.view.sections.map')}</h6>
          <MapPreview artifact={map} />
        </section>
      )}
      <EnvironmentsTable
        environments={calculation.environments}
        totals={calculation.totals || {}}
        quality={calculation.quality || {}}
        report={report}
      />
      <QualitySummary calculation={calculation} />
      <SurfaceReconciliationConsistencyWarning issues={surfaceConsistencyIssues} />
      <Warnings warnings={warnings} />
      <ReportInputSummary report={report} />
    </div>
  )
}

const DeleteReport = () => {
  const {t} = useTranslation()
  const context = useViewEconomicReportContext()
  const {load} = useEconomicReportContext()
  const {errorHandler} = useErrorHandler()
  const [deleting, setDeleting] = useState(false)

  if (!context.deleteReport) {
    return null
  }

  const handleDelete = async () => {
    setDeleting(true)
    try {
      const res = await errorHandler(ReportService.deleteEconomicReport(context.report.id))

      if (res?.ok) {
        openAlert(
          'success',
          t('reports:economic.delete.success.title'),
          t('reports:economic.delete.success.message')
        )
        load()
        context.close()
      }
    } finally {
      setDeleting(false)
    }
  }

  return (
    <ReportDeleteConfirmation
      title={t('reports:economic.delete.question')}
      confirmLabel={t('reports:economic.delete.yes_button')}
      cancelLabel={t('reports:economic.delete.no_button')}
      deleting={deleting}
      onConfirm={handleDelete}
      onCancel={() => context.setDeleteReport(false)}
    />
  )
}

const EditableReportName = ({mobile = false}) => {
  const {t} = useTranslation()
  const context = useViewEconomicReportContext()
  const {load} = useEconomicReportContext()
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(context.report.name)
  const [saving, setSaving] = useState(false)
  const normalizedName = name.trim()
  const unchanged = normalizedName === context.report.name

  useEffect(() => {
    setName(context.report.name)
    setEditing(false)
  }, [context.report.id, context.report.name])

  const cancel = () => {
    setName(context.report.name)
    setEditing(false)
  }

  const save = async (event) => {
    event.preventDefault()
    if (!normalizedName || unchanged || saving) {
      return
    }

    setSaving(true)
    const res = await context.updateName(normalizedName)
    setSaving(false)

    if (res?.ok) {
      setEditing(false)
      load(true)
      openAlert(
        'success',
        t('reports:economic.view.rename.success.title'),
        t('reports:economic.view.rename.success.message')
      )
    }
  }

  if (editing) {
    return (
      <form
        className={`economic-report-name-editor${mobile ? ' economic-report-name-editor--mobile' : ''}`}
        onSubmit={save}
      >
        <input
          type='text'
          className='form-control form-control-sm economic-report-name-editor__input'
          value={name}
          maxLength={64}
          autoFocus
          aria-label={t('reports:economic.view.rename.input_label')}
          onFocus={(event) => event.target.select()}
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              cancel()
            }
          }}
        />
        <button
          type='submit'
          className='btn btn-sm btn-primary economic-report-name-editor__action'
          disabled={!normalizedName || unchanged || saving}
        >
          {saving
            ? t('reports:economic.view.rename.saving')
            : t('reports:economic.view.rename.save')}
        </button>
        <button
          type='button'
          className='btn btn-sm btn-light economic-report-name-editor__action'
          disabled={saving}
          onClick={cancel}
        >
          {t('reports:economic.view.rename.cancel')}
        </button>
      </form>
    )
  }

  return (
    <span
      className={`economic-report-editable-name${
        mobile ? ' economic-report-editable-name--mobile' : ''
      }`}
    >
      <span className='economic-report-editable-name__text'>{context.report.name}</span>
      <button
        type='button'
        className='btn btn-icon btn-sm btn-active-light-primary economic-report-editable-name__button'
        onClick={() => setEditing(true)}
        aria-label={t('reports:economic.view.rename.edit')}
        title={t('reports:economic.view.rename.edit')}
      >
        <KTSVG path='/media/icons/duotune/art/art005.svg' className='svg-icon svg-icon-2' />
      </button>
    </span>
  )
}

const DesktopHeader = () => {
  const {t} = useTranslation()
  const context = useViewEconomicReportContext()
  const report = context.report
  const artifacts = getArtifacts(report)
  const statusLabel = t(`reports:economic.list.status.${report.status}`, report.status)

  return (
    <div className='modal-header p-5 ndvi-report-header'>
      <div className='ndvi-report-header__title-block'>
        <h5 className='modal-title ndvi-report-header__title'>
          {t('reports:economic.view.title')}
        </h5>
        <div className='ndvi-report-header__subtitle economic-report-detail-subtitle'>
          <EditableReportName />
          <span
            className={`badge ${getEconomicReportStatusClass(
              report.status
            )} economic-report-detail-status`.trim()}
          >
            {statusLabel}
          </span>
        </div>
      </div>
      <div className='ndvi-report-header__actions'>
        <DownloadButton
          artifact={artifacts.pdf}
          filename={economicArtifactFilename(report, 'pdf', t)}
          label={t('reports:economic.view.buttons.download_pdf')}
          className='btn btn-sm btn-light-primary ms-2 ndvi-report-header__action ndvi-report-header__download'
        />
        <DownloadButton
          artifact={artifacts.mapPng}
          filename={economicArtifactFilename(report, 'mapPng', t)}
          label={t('reports:economic.view.buttons.download_png')}
          className='btn btn-sm btn-light-primary ms-2 ndvi-report-header__action ndvi-report-header__download'
        />
        <button
          type='button'
          className='btn btn-sm btn-active-light-danger ms-2 ndvi-report-header__action'
          onClick={() => context.setDeleteReport(true)}
        >
          {t('reports:economic.view.buttons.delete')}
        </button>
        <div
          className='btn btn-icon btn-sm btn-active-light-primary ms-2'
          onClick={() => context.close()}
          aria-label='Close'
        >
          <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon svg-icon-2x' />
        </div>
      </div>
    </div>
  )
}

const DesktopContent = () => (
  <>
    <DesktopHeader />
    <ReportBody />
    <DeleteReport />
  </>
)

const MobileContent = () => {
  const {t} = useTranslation()
  const context = useViewEconomicReportContext()
  const report = context.report
  const artifacts = getArtifacts(report)
  const statusLabel = t(`reports:economic.list.status.${report.status}`, report.status)

  return (
    <div className='reports-mobile-shell displayable'>
      <div className='card shadow-sm right-card-data reports-mobile-card reports-mobile-card--detail'>
        <div className='reports-mobile-card__header'>
          <button
            type='button'
            className='btn btn-sm btn-light reports-mobile-card__back'
            onClick={() => context.close()}
          >
            <KTSVG path='/media/icons/duotune/arrows/arr063.svg' className='svg-icon svg-icon-2' />
            <span>{t('common:navigation.back_reports')}</span>
          </button>
          <div className='reports-mobile-card__eyebrow'>{t('reports:selector.report_label')}</div>
          <h2 className='reports-mobile-card__title economic-report-mobile-title'>
            <EditableReportName mobile />
          </h2>
          <div className='reports-mobile-card__meta'>
            <span
              className={`badge ${getEconomicReportStatusClass(
                report.status
              )} economic-report-detail-status`.trim()}
            >
              {statusLabel}
            </span>
          </div>
          <DownloadButton
            artifact={artifacts.pdf}
            filename={economicArtifactFilename(report, 'pdf', t)}
            label={t('reports:economic.view.buttons.download_pdf')}
            className='btn btn-sm btn-primary reports-mobile-card__primary-action'
          />
          <DownloadButton
            artifact={artifacts.mapPng}
            filename={economicArtifactFilename(report, 'mapPng', t)}
            label={t('reports:economic.view.buttons.download_png')}
            className='btn btn-sm btn-light-primary reports-mobile-card__primary-action'
          />
          <button
            type='button'
            className='btn btn-sm btn-light-danger reports-mobile-card__danger-action'
            onClick={() => context.setDeleteReport(true)}
          >
            {t('reports:economic.view.buttons.delete')}
          </button>
        </div>
        <div className='reports-mobile-card__body reports-mobile-card__body--detail'>
          <ReportBody mobile />
          <DeleteReport />
        </div>
      </div>
    </div>
  )
}

const ViewEconomicReportModule = () => {
  const {loading, load, report, close} = useViewEconomicReportContext()
  const {id} = useParams()
  const isMobile = useIsMobile()

  useEffect(() => {
    load(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  if (isMobile) {
    if (loading) {
      return (
        <div className='reports-mobile-shell displayable'>
          <div className='card shadow-sm right-card-data reports-mobile-card'>
            <div className='reports-mobile-card__body'>
              <Loading />
            </div>
          </div>
        </div>
      )
    }

    if (!report) {
      return null
    }

    return <MobileContent />
  }

  return (
    <Modal open={true} setOpen={close}>
      <div className='modal-dialog modal-dialog-centered modal-xl'>
        <div className={'modal-content ' + (loading ? 'p-5' : '')}>
          {loading && <Loading />}
          {!loading && report && <DesktopContent />}
        </div>
      </div>
    </Modal>
  )
}

const ViewEconomicReport = () => (
  <ViewEconomicReportProvider>
    <ViewEconomicReportModule />
  </ViewEconomicReportProvider>
)

export default ViewEconomicReport
